import React, { useCallback, useMemo, useState } from 'react';
import { Holding } from '../../types';
import Checkbox from '../ui/Checkbox';
import { formatCurrency, formatNum, formatPrice } from '../../utils/formatters';
import styles from './HoldingsTable.module.css';

interface HoldingsTableProps {
  holdings: (Holding & { id: number })[];
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  isDarkMode: boolean;
}

export default function HoldingsTable({ holdings, selectedIds, onToggle, onToggleAll, isDarkMode }: HoldingsTableProps) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [showAll, setShowAll] = useState(false);
  const DEFAULT_VISIBLE = 5;

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) {
      setSortDir((d: "asc" | "desc") => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }, [sortKey]);

  const sortedHoldings = useMemo(() => {
    if (!sortKey) return holdings;
    return [...holdings].sort((a, b) => {
      let av: number;
      let bv: number;

      if (sortKey === "stcg") {
        av = a.stcg.gain;
        bv = b.stcg.gain;
      } else if (sortKey === "ltcg") {
        av = a.ltcg.gain;
        bv = b.ltcg.gain;
      } else {
        av = (a as unknown as Record<string, number>)[sortKey];
        bv = (b as unknown as Record<string, number>)[sortKey];
      }

      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [holdings, sortKey, sortDir]);

  const visibleHoldings = showAll ? sortedHoldings : sortedHoldings.slice(0, DEFAULT_VISIBLE);
  const allSelected = selectedIds.size === holdings.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.header}>
        <h2>Holdings</h2>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <Checkbox checked={allSelected} indeterminate={someSelected} onClick={onToggleAll} />
              </th>
              <th style={{ textAlign: "left", color: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 13, fontWeight: 500 }}>Asset</th>
              <th className={styles.hideMobile}>
                <div className={styles.headerContent}>
                  <span>Holdings</span>
                  <span className={styles.subHeader}>Avg Buy Price</span>
                </div>
              </th>
              <th
                className={`${styles.hideMobilePrice} ${styles.sortable}`}
                onClick={() => handleSort("currentPrice")}
              >
                Current Price {sortKey === "currentPrice" ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </th>
              <th
                className={styles.hideMobileStcg}
                onClick={() => handleSort("stcg")}
              >
                Short-Term {sortKey === "stcg" ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </th>
              <th
                className={styles.hideMobileLtcg}
                onClick={() => handleSort("ltcg")}
              >
                Long-Term {sortKey === "ltcg" ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </th>
              <th className={styles.hideMobileSell}>Amount to Sell</th>
            </tr>
          </thead>
          <tbody>
            {visibleHoldings.map((h) => {
              const isSelected = selectedIds.has(h.id);
              return (
                <tr
                  key={h.id}
                  className={`${isSelected ? styles.rowSelected : ''}`}
                  onClick={() => onToggle(h.id)}
                >
                  <td>
                    <Checkbox checked={isSelected} />
                  </td>
                  <td>
                    <div className={styles.assetCell}>
                      <img
                        src={h.logo}
                        alt={h.coin}
                        width={32}
                        height={32}
                        className={styles.coinLogo}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                      <div>
                        <div className={styles.coinName} title={h.coinName}>
                          {h.coinName.length > 20 ? h.coin : h.coinName}
                        </div>
                        <div className={styles.coinSymbol}>{h.coin}</div>
                      </div>
                    </div>
                  </td>
                  <td className={styles.hideMobile}>
                    <div className={styles.holdingsCell}>
                      <div className={styles.holdingsAmount}>{formatNum(h.totalHolding)} {h.coin}</div>
                      <div className={styles.avgPrice}>{formatPrice(h.averageBuyPrice)}/{h.coin}</div>
                    </div>
                  </td>
                  <td className={styles.hideMobilePrice}>
                    <div className={styles.currentPrice}>{formatPrice(h.currentPrice)}</div>
                  </td>
                  <td className={styles.hideMobileStcg}>
                    <div className={`${styles.gainCell} ${h.stcg.gain >= 0 ? styles.gainPositive : styles.gainNegative}`}>
                      {formatCurrency(h.stcg.gain)}
                    </div>
                    <div className={styles.gainBalance}>{formatNum(h.stcg.balance)} {h.coin}</div>
                  </td>
                  <td>
                    <div className={`${styles.gainCell} ${h.ltcg.gain >= 0 ? styles.gainPositive : (h.ltcg.gain === 0 ? styles.gainZero : styles.gainNegative)}`}>
                      {h.ltcg.gain === 0 ? "₹0.00" : formatCurrency(h.ltcg.gain)}
                    </div>
                    <div className={styles.gainBalance}>{formatNum(h.ltcg.balance)} {h.coin}</div>
                  </td>
                  <td className={styles.hideMobile}>
                    {isSelected ? (
                      <span className={styles.amountToSell}>{formatNum(h.totalHolding)} {h.coin}</span>
                    ) : (
                      <span className={styles.amountNotSelected}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className={styles.mobileView}>
        {visibleHoldings.map((h) => {
          const isSelected = selectedIds.has(h.id);
          return (
            <div
              key={h.id}
              className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
              onClick={() => onToggle(h.id)}
            >
              <div className={styles.cardHeader}>
                <Checkbox checked={isSelected} />
                <div className={styles.assetCell}>
                  <img
                    src={h.logo}
                    alt={h.coin}
                    width={32}
                    height={32}
                    className={styles.coinLogo}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                  <div>
                    <div className={styles.coinName} title={h.coinName}>
                      {h.coinName.length > 20 ? h.coin : h.coinName}
                    </div>
                    <div className={styles.coinSymbol}>{h.coin}</div>
                  </div>
                </div>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Holdings</span>
                  <span className={styles.cardValue}>
                    {formatNum(h.totalHolding)} {h.coin}
                    <div className={styles.cardSubValue}>{formatPrice(h.averageBuyPrice)}/{h.coin}</div>
                  </span>
                </div>

                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Current Price</span>
                  <span className={styles.cardValue}>{formatPrice(h.currentPrice)}</span>
                </div>

                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Short-Term</span>
                  <span className={`${styles.cardValue} ${h.stcg.gain >= 0 ? styles.gainPositive : styles.gainNegative}`}>
                    {formatCurrency(h.stcg.gain)}
                    <div className={styles.cardSubValue}>{formatNum(h.stcg.balance)} {h.coin}</div>
                  </span>
                </div>

                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Long-Term</span>
                  <span className={`${styles.cardValue} ${h.ltcg.gain >= 0 ? (h.ltcg.gain === 0 ? styles.gainZero : styles.gainPositive) : styles.gainNegative}`}>
                    {h.ltcg.gain === 0 ? "₹0.00" : formatCurrency(h.ltcg.gain)}
                    <div className={styles.cardSubValue}>{formatNum(h.ltcg.balance)} {h.coin}</div>
                  </span>
                </div>

                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Amount to Sell</span>
                  <span className={styles.cardValue}>
                    {isSelected ? `${formatNum(h.totalHolding)} ${h.coin}` : "—"}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {holdings.length > DEFAULT_VISIBLE && (
        <div className={styles.showAllContainer}>
          <button
            onClick={() => setShowAll((v: boolean) => !v)}
            className={styles.showAllButton}
          >
            {showAll ? "Show Less" : `View All ${holdings.length} Holdings`}
          </button>
        </div>
      )}
    </div>
  );
}

